import { useMemo } from 'react'

import { useFilters, useModals, useSetState } from '@domain-group/react-hooks'
import type { UseFiltersPayload } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import BulkCreateApiKeysModal from '@modals/BulkCreateApiKeysModal'
import CreateOrEditAgencyPortalModal from '@modals/CreateOrEditAgencyPortalModal'
import CreateOrEditApiKeyModal from '@modals/CreateOrEditApiKeyModal'
import CreateOrEditIntegrationModal from '@modals/CreateOrEditIntegrationModal'
import SelectIntegrationPlatformModal from '@modals/SelectIntegrationPlatformModal'

import type { AgencyPortalModel, ApiKeyModel, IntegrationModel } from '@models/types'

import PageContent from './components/PageContent'

export type PageContextPayload = UseFiltersPayload & {
  callbacks: {
    showBulkCreateApiKeysModal: () => void,
    showCreateOrEditAgencyPortalModal: () => void,
    showCreateOrEditApiKeyModal: () => void,
    showCreateOrEditIntegrationModal: () => void,
    showSelectIntegrationPlatformModal: () => void,
  },
}

const defaultState = {
  showBulkCreateApiKeysModal: false,
  showCreateOrEditAgencyPortalModal: false,
  showCreateOrEditApiKeyModal: false,
  showCreateOrEditIntegrationModal: false,
  showSelectIntegrationPlatformModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    BulkCreateApiKeysModal: {
      bulkCreateApiKeys: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      closeModal: () => setState({ showBulkCreateApiKeysModal: false }),
    },
    CreateOrEditAgencyPortalModal: {
      closeModal: () => setState({ showCreateOrEditAgencyPortalModal: false }),
      createAgencyPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteAgencyPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      generateAgencyPortalAuthToken: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAgencyPortal: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditApiKeyModal: {
      closeModal: () => setState({ showCreateOrEditApiKeyModal: false }),
      createApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      deleteApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateApiKey: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditIntegrationModal: {
      closeModal: () => setState({ showCreateOrEditIntegrationModal: false }),
      closeAllModals: () => setState({
        showCreateOrEditIntegrationModal: false,
        showSelectIntegrationPlatformModal: false,
      }),
      createIntegration: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    SelectIntegrationPlatformModal: {
      closeModal: () => setState({ showSelectIntegrationPlatformModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showBulkCreateApiKeysModal,
    showCreateOrEditAgencyPortalModal,
    showCreateOrEditApiKeyModal,
    showCreateOrEditIntegrationModal,
    showSelectIntegrationPlatformModal,
  } = state

  const filtersPayload = useFilters({ persistFilters: true })
  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      ...filterCallbacks,
      showBulkCreateApiKeysModal: (payload: { apiKey: Partial<ApiKeyModel> }) => {
        setModalData('BulkCreateApiKeysModal', payload)
        setState({ showBulkCreateApiKeysModal: true })
      },
      showCreateOrEditAgencyPortalModal: (payload: Partial<AgencyPortalModel>) => {
        setModalData('CreateOrEditAgencyPortalModal', payload)
        setState({ showCreateOrEditAgencyPortalModal: true })
      },
      showCreateOrEditApiKeyModal: (payload: { apiKey: ApiKeyModel }) => {
        setModalData('CreateOrEditApiKeyModal', payload)
        setState({ showCreateOrEditApiKeyModal: true })
      },
      showCreateOrEditIntegrationModal: (payload: Partial<AgencyPortalModel>) => {
        setModalData('CreateOrEditIntegrationModal', payload)
        setState({ showCreateOrEditIntegrationModal: true })
      },
      showSelectIntegrationPlatformModal: (payload: { integration: IntegrationModel }) => {
        setModalData('SelectIntegrationPlatformModal', payload)
        setState({ showSelectIntegrationPlatformModal: true })
      },
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt]) // modalContext dependency required if layering modals

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        {/*
         // @ts-expect-error TS2322 */}
        <PageContent pageFilters={pageFilters} />

        <BulkCreateApiKeysModal
          // @ts-expect-error TS2322
          callbacks={callbacks('BulkCreateApiKeysModal', setState)}
          showModal={showBulkCreateApiKeysModal}
        />

        <CreateOrEditAgencyPortalModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAgencyPortalModal', setState)}
          showModal={showCreateOrEditAgencyPortalModal}
        />

        <CreateOrEditApiKeyModal
          callbacks={callbacks('CreateOrEditApiKeyModal', setState)}
          showModal={showCreateOrEditApiKeyModal}
        />

        <CreateOrEditIntegrationModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditIntegrationModal', setState)}
          showModal={showCreateOrEditIntegrationModal}
        />

        <SelectIntegrationPlatformModal
          callbacks={callbacks('SelectIntegrationPlatformModal', setState)}
          showModal={showSelectIntegrationPlatformModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
